import { defineStore } from "pinia";
import { ApplicationScope, Message, Organisation, Product, Voucher, VoucherBulk } from "~/api-client";
import { errorMsg, isExpired } from "~/modules/common/services/util/util";
import { useAuthStore } from "~/modules/common/stores/auth";
import { Purchasable } from "~/modules/common/stores/products";

interface State {
  _allVouchers: Voucher[],
  _voucherByCode: { [code: string]: Voucher };

}
export const useVoucherStore = defineStore('voucher', {
  state: (): State => ({
    _allVouchers: [],
    _voucherByCode: {}
  }),
  getters: {
    allVouchers: (state) => (state._allVouchers),
    voucherByCode: (state) => (code: string) => state._voucherByCode[code],
  },
  actions: {
    setAllVouchersToVoucherByCode(){
      let voucherByCode: { [code: string]: Voucher } = {};
      this.$state._allVouchers.forEach((key: Voucher) => {
        voucherByCode[key.code!] = key!;
      });
      this.$state._voucherByCode = voucherByCode;
    },
    setVouchersToVoucherByCode(vouchers: Voucher[]){
      vouchers.forEach((key: Voucher) => {
        this.$state._voucherByCode[key.code!] = key!;
      });
    },
    getAllVouchers(){
      const {$backendService, $logger, $authStore} = useNuxtApp();

      let vouchersCall;
      if ($authStore.isCurrentUserSuperAdmin) {
        vouchersCall = $backendService.voucherControllerApi.getVouchers({xApplicationScope: ApplicationScope.MyDb});
      } else {
        vouchersCall = $backendService.voucherControllerApi.getVouchersForOrganisation({xApplicationScope: ApplicationScope.MyDb});
      }

      return vouchersCall.then(result => {

        result.sort((voucher1: Voucher, voucher2: Voucher) => {
          let expiry1: number = !!voucher1.expirationDate ? voucher1.expirationDate.valueOf() : 0;
          let expiry2: number = !!voucher2.expirationDate ? voucher2.expirationDate.valueOf() : 0;
          return (expiry1 < expiry2) ? 1 : (expiry1 > expiry2) ? -1 : 0;
        });
        this.$state._allVouchers = result;
        return result;
      }).catch(error => {
        $logger.error("error loading vouchers: ", error )
        ElMessage.error('Beim Laden der Vouchers sind zu einem Fehler gekommen.');
        return <Voucher[]>[];

      });
    },
    exportAllVouchers(){
      const {$backendService, $logger, $authStore} = useNuxtApp();
      let exportMethod;

      if ($authStore.isCurrentUserSuperAdmin) {
        exportMethod = $backendService.voucherControllerApi.getVouchersAsExcel({xApplicationScope: ApplicationScope.MyDb});
      } else {
        exportMethod = $backendService.voucherControllerApi.getVouchersForOrganisationAsExcel({xApplicationScope: ApplicationScope.MyDb});
      }
      return exportMethod;
    },
    bulkCreateVouchers(voucherBulk: VoucherBulk){
      const {$backendService, $logger} = useNuxtApp();
      return $backendService.voucherControllerApi.bulkCreateVouchers({
        xApplicationScope: ApplicationScope.MyDb,
        voucherBulk: voucherBulk
      }).catch(error => {
        ElMessage.error("Beim Anlegen der Voucher ist es zu einem Fehler gekommen");
        return <Voucher[]>[];
      })
    },
    async partiallyUpdateVoucher(voucher: Voucher){
      const {$backendService, $logger} = useNuxtApp();
      return $backendService.voucherControllerApi.partiallyUpdateVoucher({
        xApplicationScope: ApplicationScope.MyDb,
        voucher: voucher
      }).catch(error => {
        ElMessage.error("Beim Zuweisen des Benutzers/Gruppe ist ein Fehler augetreten");
        console.log('error', error);
        return <Voucher>{};
      })
    },
    deleteVoucher(voucher: Voucher){
      const {$backendService, $logger} = useNuxtApp();

      return $backendService.voucherControllerApi.deleteVoucher({
        xApplicationScope: ApplicationScope.MyDb,
        id: voucher.id!
      }).catch(error => {
        errorMsg(error).then(err => ElMessage.error("Beim Löschen des Vouchers kam es zu einem Fehler."));
      })
    },
    createVouchersInAllVouchersState(vouchers: Voucher[]){
      if (!!vouchers){
        this.$state._allVouchers.push(...vouchers);
      }
      this.setVouchersToVoucherByCode(vouchers);
    },
    updateVoucherInAllVouchersState(voucherId: string, voucherToBeUpdated?: Voucher | null){
      if (!!voucherToBeUpdated){
        this.$state._allVouchers = this.$state._allVouchers.map(voucher => {
          if (voucher.id === voucherId){
            return voucherToBeUpdated!;
          }
          return voucher;
        })
      } else {
        this.$state._allVouchers = this.$state._allVouchers.reduce((filtered: Voucher[], voucher: Voucher) => {
          if (voucher.id !== voucherId) {
            filtered.push(voucher);
          }
          return filtered;
        }, []);
      }
    }
  }
})

export interface VoucherProcessingData extends Voucher{
  quantity: number,
}